/**
 * Temporal ball gate + smoother.
 *
 * Single-frame detectors (TrackNet, motion, COCO) produce jittery output with
 * the occasional wild outlier (a bright graphic, a mis-fire). This tracker:
 *   - rejects detections that jump implausibly far given the elapsed time
 *     (outlier suppression) — with re-acquisition after several rejects, so a
 *     genuine scene change / fast serve isn't rejected forever;
 *   - applies light exponential smoothing to reduce positional jitter.
 *
 * Coordinates are in source pixels; jump limits are normalized by frame width.
 */
export class BallTracker {
  constructor(opts = {}) {
    this.maxSpeed = opts.maxSpeed ?? 3.5; // frame-widths per second allowed
    this.baseJump = opts.baseJump ?? 0.1; // base allowance (frac of width)
    this.maxMisses = opts.maxMisses ?? 5; // rejects/misses before re-acquiring
    this.smoothing = opts.smoothing ?? 0.6; // EMA weight on the new sample
    this.reset();
  }

  reset() {
    this.last = null; // { x, y, t }
    this.misses = 0;
  }

  /**
   * @param ball  {x,y,score} in source px, or null when nothing detected
   * @param w,h   source frame dimensions (px)
   * @param t     current time (seconds)
   * @returns accepted/smoothed {x,y,score} or null
   */
  update(ball, w, h, t) {
    if (!ball) {
      this.misses++;
      if (this.misses > this.maxMisses) this.last = null;
      return null;
    }

    if (this.last) {
      const dt = Math.max(1 / 60, t - this.last.t);
      const dist = Math.hypot(ball.x - this.last.x, ball.y - this.last.y) / w;
      const allowed = this.baseJump + this.maxSpeed * dt;
      if (dist > allowed && this.misses < this.maxMisses) {
        this.misses++;
        return null; // outlier — drop this frame, keep prior anchor
      }
    }

    this.misses = 0;
    let sx = ball.x;
    let sy = ball.y;
    if (this.last) {
      const a = this.smoothing;
      sx = a * ball.x + (1 - a) * this.last.x;
      sy = a * ball.y + (1 - a) * this.last.y;
    }
    this.last = { x: sx, y: sy, t };
    return { x: sx, y: sy, score: ball.score };
  }
}
